import { useState, useRef, useEffect } from "react";
import {
  FiChevronDown,
  FiChevronUp,
  FiSearch,
  FiBell,
  FiExternalLink,
  FiX,
  FiGrid,
  FiSettings,
  FiCalendar,
  FiClock,
  FiPlus,
} from "react-icons/fi";
import ModernTabs from "./ModernTabs";

const formatDate = (timestamp) => {
  if (!timestamp) return "";
  // Convert string timestamp to number before creating Date object
  const date = new Date(Number(timestamp));

  // Format date with time
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${day}-${month}-${year} at ${hours}:${minutes}`;
};

const TopHeader = ({
  projects = [],
  selectedProject,
  setSelectedProject,
  selectedTab,
  setSelectedTab,
  searchQuery,
  setSearchQuery,
  onAddTask,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isHoveringTab, setIsHoveringTab] = useState(null);
  const [projectDropdownSearch, setProjectDropdownSearch] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const tabs = ["BOQ timeline", "Project timeline"];

  // Add animation keyframes
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes pulse {
        0% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.1); opacity: 0.8; }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes expandWidth {
        from { width: 60%; opacity: 0.7; }
        to { width: 100%; opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    // Select first project if none selected
    if (projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0]);
    }

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [projects, selectedProject, setSelectedProject]);

  useEffect(() => {
    // Focus search input when opened
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setIsDropdownOpen(false);
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (!isSearchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } else {
      setSearchQuery("");
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setIsSearchOpen(false);
  };

  // Generate a color based on project name
  const getProjectColor = (name) => {
    if (!name) return { bg: "#f3f4f6", text: "#6b7280" };

    const colors = [
      {
        bg: "#EFF6FF",
        text: "#1E40AF",
        gradient: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
      }, // Blue
      {
        bg: "#F0FDF4",
        text: "#166534",
        gradient: "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)",
      }, // Green
      {
        bg: "#FDF2F8",
        text: "#9D174D",
        gradient: "linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)",
      }, // Pink
      {
        bg: "#FFFBEB",
        text: "#92400E",
        gradient: "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)",
      }, // Yellow
      {
        bg: "#EFF6FF",
        text: "#1E3A8A",
        gradient: "linear-gradient(135deg, #EFF6FF 0%, #BFDBFE 100%)",
      }, // Indigo
      {
        bg: "#F5F3FF",
        text: "#5B21B6",
        gradient: "linear-gradient(135deg, #F5F3FF 0%, #E9D5FF 100%)",
      }, // Purple
      {
        bg: "#ECFDF5",
        text: "#065F46",
        gradient: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)",
      }, // Emerald
      {
        bg: "#F0FDFA",
        text: "#0F766E",
        gradient: "linear-gradient(135deg, #F0FDFA 0%, #CCFBF1 100%)",
      }, // Teal
    ];

    const hash = Array.from(name).reduce(
      (acc, char) => acc + char.charCodeAt(0),
      0
    );
    return colors[hash % colors.length];
  };

  const filteredProjects = projects.filter((project) =>
    project.ClientName.toLowerCase().includes(
      projectDropdownSearch.toLowerCase()
    )
  );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 28px",
        backgroundColor: "#ffffff",
        height: "76px",
        boxShadow:
          "rgba(0, 0, 0, 0.06) 0px 2px 8px 0px, rgba(0, 0, 0, 0.03) 0px 1px 2px 0px",
        position: "relative",
        zIndex: 10,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        borderBottom: "1px solid rgba(229, 231, 235, 0.5)",
        background: "linear-gradient(to bottom, #ffffff, #fafbfc)",
      }}
    >
      {/* Left Side - Project Dropdown */}
      <div
        style={{
          position: "relative",
          minWidth: "280px",
          marginRight: "32px",
        }}
        ref={dropdownRef}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "8px 8px 8px 8px",
            borderRadius: "12px",
            cursor: "pointer",
            transition: "all 0.2s ease",
            backgroundColor: isDropdownOpen
              ? "rgba(249, 250, 251, 0.8)"
              : "transparent",
            border: isDropdownOpen
              ? "1px solid rgba(229, 231, 235, 0.5)"
              : "1px solid transparent",
            boxShadow: isDropdownOpen
              ? "rgba(0, 0, 0, 0.05) 0px 1px 3px 0px"
              : "none",
          }}
          onClick={toggleDropdown}
        >
          {selectedProject && (
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "10px",
                background: selectedProject.ClientName
                  ? getProjectColor(selectedProject.ClientName).gradient
                  : getProjectColor("").gradient,
                color: selectedProject.ClientName
                  ? getProjectColor(selectedProject.ClientName).text
                  : getProjectColor("").text,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "600",
                fontSize: "16px",
                marginRight: "12px",
                boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px 0px",
              }}
            >
              {selectedProject.ClientName
                ? selectedProject.ClientName.charAt(0)
                : "?"}
            </div>
          )}

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
            }}
          >
            <h2
              style={{
                fontSize: "15px",
                fontWeight: "600",
                color: "#111827",
                margin: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {selectedProject?.ClientName || "Select Project"}
            </h2>
            {selectedProject?.lastOpen && (
              <p
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  margin: "3px 0 0 0",
                  fontWeight: "400",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FiClock
                  size={10}
                  style={{ marginRight: "4px", opacity: 0.7 }}
                />
                Modified on {formatDate(selectedProject.lastOpen)}
              </p>
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "28px",
              width: "28px",
              borderRadius: "8px",
              marginLeft: "8px",
              backgroundColor: isDropdownOpen
                ? "rgba(229, 231, 235, 0.5)"
                : "transparent",
              color: "#6b7280",
              transition: "all 0.2s ease",
            }}
          >
            {isDropdownOpen ? (
              <FiChevronUp size={16} />
            ) : (
              <FiChevronDown size={16} />
            )}
          </div>
        </div>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              left: 0,
              width: "320px",
              backgroundColor: "white",
              borderRadius: "16px",
              boxShadow:
                "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
              overflow: "hidden",
              zIndex: 30,
              border: "1px solid rgba(229, 231, 235, 0.7)",
              animation: "fadeIn 0.2s ease",
            }}
          >
            <div
              style={{
                padding: "16px 16px 12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid rgba(229, 231, 235, 0.7)",
                background: "linear-gradient(to right, #fafafa, #ffffff)",
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#4b5563",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FiCalendar
                  size={12}
                  style={{ marginRight: "6px", opacity: 0.7 }}
                />
                Recent Projects
              </span>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    background:
                      "linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: "rgba(0, 0, 0, 0.02) 0px 1px 2px 0px",
                    border: "1px solid rgba(229, 231, 235, 0.3)",
                  }}
                >
                  <FiGrid size={14} color="#6b7280" />
                </div>
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    background:
                      "linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: "rgba(0, 0, 0, 0.02) 0px 1px 2px 0px",
                    border: "1px solid rgba(229, 231, 235, 0.3)",
                  }}
                >
                  <FiSettings size={14} color="#6b7280" />
                </div>
              </div>
            </div>

            <div
              style={{
                padding: "8px 16px",
                borderBottom: "1px solid rgba(229, 231, 235, 0.2)",
                background: "#fafafa",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FiSearch size={16} color="#9ca3af" />
              <input
                type="text"
                placeholder="Search projects..."
                value={projectDropdownSearch}
                onChange={(e) => setProjectDropdownSearch(e.target.value)}
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  fontSize: "14px",
                  padding: "6px 0",
                  backgroundColor: "transparent",
                  color: "#111827",
                }}
                autoFocus
              />
              {projectDropdownSearch && (
                <FiX
                  size={16}
                  color="#9ca3af"
                  style={{ cursor: "pointer" }}
                  onClick={() => setProjectDropdownSearch("")}
                />
              )}
            </div>
            {/* --- END SEARCH INPUT --- */}

            <div
              style={{
                maxHeight: "400px",
                overflowY: "auto",
                padding: "8px 0",
              }}
            >
              {filteredProjects.length === 0 ? (
                <div
                  style={{
                    padding: "16px",
                    color: "#9ca3af",
                    textAlign: "center",
                  }}
                >
                  No projects found.
                </div>
              ) : (
                filteredProjects.map((project, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "10px 16px",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      backgroundColor:
                        selectedProject?.ClientName === project.ClientName
                          ? "rgba(249, 250, 251, 0.8)"
                          : "transparent",
                      borderLeft:
                        selectedProject?.ClientName === project.ClientName
                          ? "3px solid #fdd34d"
                          : "3px solid transparent",
                    }}
                    onClick={() => handleProjectSelect(project)}
                  >
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "10px",
                        background: project.ClientName
                          ? getProjectColor(project.ClientName).gradient
                          : getProjectColor("").gradient,
                        color: project.ClientName
                          ? getProjectColor(project.ClientName).text
                          : getProjectColor("").text,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "600",
                        fontSize: "16px",
                        marginRight: "12px",
                        boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px 0px",
                      }}
                    >
                      {project.ClientName ? project.ClientName.charAt(0) : "?"}
                    </div>

                    <div
                      style={{
                        flex: 1,
                      }}
                    >
                      <p
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#111827",
                          margin: 0,
                        }}
                      >
                        {project.ClientName || "Unnamed Project"}
                      </p>
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          margin: "3px 0 0 0",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <FiClock
                          size={10}
                          style={{ marginRight: "4px", opacity: 0.7 }}
                        />
                        Modified on {formatDate(project.lastOpen)}
                      </p>
                    </div>

                    {selectedProject?.ClientName === project.ClientName && (
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, #fdd34d 0%, #f59e0b 100%)",
                          marginLeft: "8px",
                          boxShadow: "0 0 5px rgba(253, 211, 77, 0.5)",
                        }}
                      />
                    )}
                  </div>
                ))
              )}
            </div>

            <div
              style={{
                padding: "12px 16px",
                borderTop: "1px solid rgba(229, 231, 235, 0.7)",
                display: "flex",
                justifyContent: "center",
                background: "linear-gradient(to right, #fafafa, #ffffff)",
              }}
            >
              <button
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "8px 16px",
                  borderRadius: "10px",
                  background:
                    "linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)",
                  border: "1px solid rgba(229, 231, 235, 0.5)",
                  fontWeight: "500",
                  fontSize: "13px",
                  color: "#4b5563",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  width: "100%",
                  boxShadow: "rgba(0, 0, 0, 0.02) 0px 1px 2px 0px",
                }}
              >
                <FiGrid size={14} style={{ marginRight: "8px" }} />
                View All Projects
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Middle - Tabs or Search */}
      <div
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          transition: "all 0.3s ease",
        }}
      >
        {isSearchOpen ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              background: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)",
              borderRadius: "16px",
              padding: "0 16px",
              width: "100%",
              height: "48px",
              maxWidth: "960px", // Increased width
              animation: "expandWidth 0.3s ease",
              border: "1px solid rgba(99, 102, 241, 0.2)",
              transition: "all 0.2s ease",
              boxShadow:
                "rgba(79, 70, 229, 0.1) 0px 1px 6px 0px, rgba(0, 0, 0, 0.05) 0px 1px 3px 0px",
              position: "relative",
            }}
          >
            {/* Search Icon */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                borderRadius: "10px",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                marginRight: "12px",
                boxShadow: "rgba(79, 70, 229, 0.1) 0px 1px 3px 0px",
                border: "1px solid rgba(99, 102, 241, 0.1)",
              }}
            >
              <FiSearch
                size={16}
                style={{
                  color: "#4f46e5",
                }}
              />
            </div>

            {/* Search Input */}
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search for tasks, milestones, or documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                border: "none",
                backgroundColor: "transparent",
                fontSize: "14px",
                fontWeight: "400",
                color: "#111827",
                padding: "12px 0",
                outline: "none",
              }}
            />

            {/* Clear Search Button (when there's input) */}
            {searchQuery && (
              <div
                onClick={clearSearch}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  borderRadius: "10px",
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "rgba(79, 70, 229, 0.1) 0px 1px 3px 0px",
                  border: "1px solid rgba(99, 102, 241, 0.1)",
                  marginRight: "8px",
                }}
              >
                <FiX size={16} color="#4f46e5" />
              </div>
            )}

            {/* Close Search Button (always visible) */}
            <div
              onClick={toggleSearch}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "rgba(79, 70, 229, 0.2) 0px 2px 4px 0px",
                border: "1px solid rgba(79, 70, 229, 0.1)",
              }}
            >
              <FiX size={16} color="#ffffff" />
            </div>
          </div>
        ) : (
          <ModernTabs
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            tabs={tabs}
            isHoveringTab={isHoveringTab}
            setIsHoveringTab={setIsHoveringTab}
          />
          // <div
          //   style={{
          //     display: "flex",
          //     alignItems: "center",
          //     gap: "48px", // Increased gap between tabs
          //   }}
          // >
          //   {tabs.map((tab) => (
          //     <div
          //       key={tab}
          //       style={{
          //         display: "flex",
          //         flexDirection: "column",
          //         alignItems: "center",
          //         fontSize: "14px",
          //         fontWeight: selectedTab === tab ? "600" : "500",
          //         color: selectedTab === tab ? "#111827" : "#6b7280",
          //         cursor: "pointer",
          //         padding: "8px 4px",
          //         position: "relative",
          //         transition: "all 0.2s ease",
          //         letterSpacing: "0.01em",
          //       }}
          //       onClick={() => setSelectedTab(tab)}
          //       onMouseEnter={() => setIsHoveringTab(tab)}
          //       onMouseLeave={() => setIsHoveringTab(null)}
          //     >
          //       {tab}
          //       <div
          //         style={{
          //           position: "absolute",
          //           bottom: "-17px",
          //           left: "0",
          //           right: "0",
          //           height: "3px",
          //           background:
          //             selectedTab === tab
          //               ? "linear-gradient(to right, #fdd34d, #f59e0b)"
          //               : "linear-gradient(to right, #fdd34d, #f59e0b)",
          //           borderRadius: "2px 2px 0 0",
          //           opacity:
          //             selectedTab === tab ? 1 : isHoveringTab === tab ? 0.3 : 0,
          //           transform: `scaleX(${
          //             selectedTab === tab ? 1 : isHoveringTab === tab ? 0.6 : 0
          //           })`,
          //           transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          //           boxShadow:
          //             selectedTab === tab
          //               ? "0 0 8px rgba(253, 211, 77, 0.4)"
          //               : "none",
          //         }}
          //       />
          //     </div>
          //   ))}
          // </div>
        )}
      </div>

      {/* Right Side - Action Icons */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
        }}
      >
        {/* Add Task Button */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            padding: "8px 16px",
            borderRadius: "12px",
            cursor: "pointer",
            color: "#ffffff",
            transition: "all 0.2s ease",
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            border: "1px solid rgba(16, 185, 129, 0.3)",
            boxShadow: "rgba(16, 185, 129, 0.25) 0px 2px 5px 0px",
            transform: "scale(1)",
            fontSize: "14px",
            fontWeight: "500",
          }}
          onClick={onAddTask}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.boxShadow = "rgba(16, 185, 129, 0.35) 0px 4px 8px 0px";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "rgba(16, 185, 129, 0.25) 0px 2px 5px 0px";
          }}
        >
          <FiPlus size={18} />
          <span>Add Task</span>
        </div>

        {/* Search Icon Button */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            borderRadius: "12px",
            cursor: "pointer",
            color: isSearchOpen ? "#4f46e5" : "#6b7280",
            transition: "all 0.2s ease",
            background: isSearchOpen
              ? "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)"
              : "linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)",
            border: isSearchOpen
              ? "1px solid rgba(99, 102, 241, 0.2)"
              : "1px solid rgba(229, 231, 235, 0.3)",
            boxShadow: isSearchOpen
              ? "rgba(79, 70, 229, 0.15) 0px 2px 5px 0px"
              : "rgba(0, 0, 0, 0.02) 0px 1px 2px 0px",
            transform: isSearchOpen ? "scale(1.05)" : "scale(1)",
          }}
          onClick={toggleSearch}
        >
          <FiSearch size={20} />
        </div>

        {/* External Link Button */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            borderRadius: "12px",
            cursor: "pointer",
            color: "#6b7280",
            transition: "all 0.2s ease",
            background: "linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)",
            boxShadow: "rgba(0, 0, 0, 0.02) 0px 1px 2px 0px",
            border: "1px solid rgba(229, 231, 235, 0.3)",
            transform: "scale(1)",
            "&:hover": {
              transform: "scale(1.05)",
              boxShadow: "rgba(0, 0, 0, 0.05) 0px 2px 5px 0px",
            },
          }}
        >
          <FiExternalLink size={20} />
        </div>

        {/* Notification Bell Button with Indicator */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            borderRadius: "12px",
            cursor: "pointer",
            color: "#6b7280",
            transition: "all 0.2s ease",
            background: "linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)",
            boxShadow: "rgba(0, 0, 0, 0.02) 0px 1px 2px 0px",
            border: "1px solid rgba(229, 231, 235, 0.3)",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "6px",
              right: "8px",
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #ec4899 0%, #be185d 100%)",
              border: "2px solid white",
              boxShadow: "0 0 6px rgba(236, 72, 153, 0.6)",
              animation: "pulse 2s infinite",
            }}
          />
          <FiBell size={20} />
        </div>

        {/* User Avatar Button */}
        <div
          style={{
            width: "42px",
            height: "42px",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: "600",
            color: "#ffffff",
            letterSpacing: "0.02em",
            boxShadow: "rgba(79, 70, 229, 0.25) 0px 4px 12px 0px",
            border: "2px solid rgba(255, 255, 255, 0.8)",
            transition: "all 0.2s ease",
            transform: "scale(1)",
            "&:hover": {
              transform: "scale(1.05)",
              boxShadow: "rgba(79, 70, 229, 0.35) 0px 6px 16px 0px",
            },
          }}
        >
          {selectedProject && selectedProject.ClientName[0]}
        </div>
      </div>
    </div>
  );
};

export default TopHeader;
